import { useMemo } from 'react';
import dynamic from 'next/dynamic';

import {
  DoctorActivityStatusEnum,
  DoctorProfile,
  DoctorTarif,
} from '@models/ocs/doctor';
import Icon, { IconName } from '@utilities/icon';
import { ServiceIdEnum, serviceIdEnum } from '@models/ocs/doctor';
import NotifyOnlineDoctor from '@containers/doctor/profile/[id]/_components/notify-online-doctor/notify-online-doctor';
import { useGetDoctorClinic } from '@api/in-person-booking/index.rq';
import { useGetDoctorServiceTarifs } from '@api/ocs/doctor/index.rq';
import { convertRialToToman } from '@utilities/ocs/numberExtensions';
import { colors } from '@configs/Theme';
import useInView from '@hooks/useInView';
import useModal from '@hooks/useModal';
import useServiceBooking from '@hooks/useServiceBooking';
import { colorPallete } from '@configs/color-pallete';

import ShimmerConsultationTypes from './shimmer';
import TariffBottomSheet from '../Tariff-bottom-sheet/Tariff-bottom-sheet';

const ConsultationTypeCard = dynamic(
  () => import('../consultationTypeCard/consultationTypeCard'),
);

export type ConsultationTypeCard = {
  id: number;
  title: string;
  price: string;
  tarifs: DoctorTarif[];
  serviceType: number;
  primaryLabel?: string;
  secondaryLabel?: string;
  iconName: IconName;
  activeIconName: IconName;
  iconColor: string;
  currencyType?: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  serviceId: ServiceIdEnum;
};

type ConsultationTypeProps = {
  activityStatus?: DoctorActivityStatusEnum;
  isDoctorOnline?: 0 | 1;
  doctorId: number;
  specialityId: number;
  doctorProfileResponse: DoctorProfile;
  subLatestSpecialityId: number;
  systemType: number;
  hasSchedule: boolean;
  showNotifyOnlineDoctor: boolean;
  isFromNobat?: boolean;
};

const ConsultationTypes = ({
  isDoctorOnline,
  doctorId,
  specialityId,
  doctorProfileResponse,
  subLatestSpecialityId,
  systemType,
  hasSchedule,
  showNotifyOnlineDoctor = true,
  isFromNobat = false,
}: ConsultationTypeProps) => {
  const { addModal } = useModal();

  const { ref: serviceTarifsRef, isInView: serviceTarifsIsInView } =
    useInView();

  const { userServiceData } = useServiceBooking({
    doctorId,
    doctorProfile: doctorProfileResponse,
    profileId: doctorProfileResponse?.profileId,
  });

  const { data: clinicData } = useGetDoctorClinic(
    { doctorId: String(doctorId) },
    {
      enabled: !!doctorId,
    },
  );

  const { data, isLoading, isPending } = useGetDoctorServiceTarifs(
    doctorId,
    subLatestSpecialityId,
    systemType,
    hasSchedule,
    { enabled: !!doctorId && serviceTarifsIsInView },
  );

  const tarifsData = data?.result;

  const serviceIconName = {
    ondemandCall: 'PhoneOndemand',
    scheduledCall: 'PhoneScheduled',
    ondemandChat: 'DialogBoxTextLeftFillRound',
    inPersonVisit: 'HomeHealth',
  };

  const serviceIconColor = {
    ondemandCall: colorPallete?.content?.positive,
    scheduledCall: colorPallete?.content?.warning,
    ondemandChat: colors?.indigo?.[300],
    inPersonVisit: colorPallete?.content?.accent,
  };

  const serviceIconBackgroundColor = {
    ondemandCall: 'bg-surface-positiveLight',
    scheduledCall: 'bg-surface-warningLight',
    ondemandChat: 'bg-indigo-50',
    inPersonVisit: 'bg-surface-accentLight',
  };

  const types = useMemo(() => {
    const activeTarifsData = tarifsData?.filter((service) => service.isActive);

    const sortedTarifsData =
      isFromNobat && activeTarifsData?.length
        ? [...activeTarifsData].sort((a, b) => {
            if (a.id === ServiceIdEnum.inPersonVisit) return -1;
            if (b.id === ServiceIdEnum.inPersonVisit) return 1;
            return 0;
          })
        : activeTarifsData;

    return sortedTarifsData?.map((service) => {
      const finalFee = service?.tarifs?.length
        ? Math.min(...service.tarifs.map((item) => item.finalFee))
        : 0;

      return {
        firstDurationMinutes: service.tarifs[0]?.durationMinute ?? 30,
        id: service.id,
        title: service.title,
        price: convertRialToToman(finalFee).toString(),
        primaryLabel: service.description,
        iconName: serviceIconName[serviceIdEnum[service?.id]],
        iconColor: service?.isActive
          ? serviceIconColor[serviceIdEnum[service?.id]]
          : colorPallete?.content?.primary,
        iconBackground: service?.isActive
          ? serviceIconBackgroundColor[serviceIdEnum[service?.id]]
          : 'bg-surface-tertiary',
        currencyType: 'تومان',
        serviceType: service.serviceType,
        serviceId: service?.id,
        isActive: service?.isActive,
      };
    });
  }, [tarifsData, isFromNobat]);

  const handleIsTariffsBottomSheetOpen = () => {
    addModal({
      modal: TariffBottomSheet,
    });
  };

  const shouldShowNotifyCard =
    showNotifyOnlineDoctor &&
    !isDoctorOnline &&
    !hasSchedule &&
    !userServiceData?.queryResult?.length &&
    !clinicData?.data?.length;
  const hasActiveServices = (types?.length ?? 0) > 0;

  if (!isLoading && !isPending && !hasActiveServices && !shouldShowNotifyCard) {
    return null;
  }

  return (
    <div
      id="consultantionTypes"
      className={`w-full p-4 scroll-mt-[42px] flex flex-col gap-y-4 justify-end ${
        hasActiveServices || isLoading || isPending ? 'min-h-[106px]' : ''
      }`}
      ref={serviceTarifsRef}
    >
      {shouldShowNotifyCard && (
        <NotifyOnlineDoctor doctorId={doctorId} specialityId={specialityId} />
      )}

      {(hasActiveServices || isLoading || isPending) &&
        (isLoading || isPending ? (
          <ShimmerConsultationTypes />
        ) : (
          <div className="w-full flex flex-col gap-y-4">
            {types?.map((type) => {
              return (
                <ConsultationTypeCard
                  key={type.id}
                  title={type?.title}
                  price={type?.price}
                  durationMinute={type?.firstDurationMinutes}
                  primaryLabel={type?.primaryLabel}
                  iconName={type?.iconName}
                  iconColor={type?.iconColor}
                  iconBackground={type?.iconBackground}
                  currencyType={type?.currencyType}
                  isActive={type?.isActive}
                  serviceId={type?.serviceId}
                  doctorProfileResponse={doctorProfileResponse}
                />
              );
            })}
          </div>
        ))}

      {hasActiveServices && (
        <button
          onClick={() => handleIsTariffsBottomSheetOpen()}
          className="flex justify-between items-center border border-border-accent bg-surface-accentLight rounded-lg px-4 py-[10px] w-full"
        >
          <div className="flex gap-x-2">
            <Icon
              name="CircleInformationFill"
              width={1.5}
              height={1.5}
              fill={colorPallete?.content?.accent}
            />
            <span className="text-Body/xSmall">
              پایین تر از تعرفه رسمی ۱۴۰۵
            </span>
          </div>
          <Icon name="ChevronLeft" width={1.5} height={1.5} />
        </button>
      )}
    </div>
  );
};

export default ConsultationTypes;
